import { gql } from '@apollo/client';

// ============================================================================
// EMAIL MUTATIONS
// Mutations for adding, deleting, and verifying customer email addresses.
// ============================================================================

/**
 * ADD_EMAILS_MUTATION
 *
 * Adds one or more email addresses to the customer's account.
 *
 * @param address   - The email address to add
 * @param customerId - The customer's ID (currentCustomer.id)
 * @param emailType - EmailTypeId enum: MAIN, INFORMATION, INVOICE_STATEMENT, etc.
 * @param sortOrder - Display sort order (defaults to 1)
 *
 * @returns List of added emails with id, address, verified status and type
 */
export const ADD_EMAILS_MUTATION = gql`
	mutation AddEmails(
		$address: String!
		$customerId: Int!
		$emailType: EmailTypeId!
		$sortOrder: Byte!
	) {
		addEmails(
			input: {
				emails: {
					address: $address
					customerId: $customerId
					emailType: $emailType
					sortOrder: $sortOrder
				}
			}
		) {
			email {
				address
				id
				verified
				verifiedAt
				type {
					id
					name
				}
			}
		}
	}
`;

// ============================================================================
// UPDATE EMAILS MUTATION
// ============================================================================

/**
 * UPDATE_EMAILS_MUTATION
 *
 * Updates an existing email address on the customer's account.
 *
 * @param emailId   - The ID of the existing email to update
 * @param address   - The new email address
 * @param emailType - EmailTypeId enum: MAIN, INFORMATION, INVOICE_STATEMENT, etc.
 * @param sortOrder - Display sort order (defaults to 1)
 *
 * @returns Updated list of emails with id, address, verified status and type
 */
export const UPDATE_EMAILS_MUTATION = gql`
	mutation UpdateEmails(
		$emailId: Int!
		$customerId: Int!
		$address: String!
		$emailType: EmailTypeId!
		$sortOrder: Byte!
	) {
		updateEmails(
			input: {
				emails: {
					emailId: $emailId
					customerId: $customerId
					address: $address
					emailType: $emailType
					sortOrder: $sortOrder
				}
			}
		) {
			email {
				address
				id
				verified
				verifiedAt
				type {
					id
					name
				}
			}
		}
	}
`;

// ============================================================================
// DELETE EMAILS MUTATION (prepared for future implementation)
// ============================================================================

/**
 * DELETE_EMAILS_MUTATION
 *
 * Deletes one or more email addresses from the customer's account.
 *
 * @param emailIds - The ID(s) of the email(s) to delete
 *
 * @returns emailDeletePayload containing the deleted emailIds
 */
export const DELETE_EMAILS_MUTATION = gql`
	mutation DeleteEmails($emailIds: [Int!]!) {
		deleteEmails(input: { emailIds: $emailIds }) {
			emailDeletePayload {
				emailIds
			}
		}
	}
`;

// ============================================================================
// VERIFY EMAIL MUTATION (prepared for future implementation)
// ============================================================================

/**
 * VERIFY_EMAIL_MUTATION
 *
 * Verifies an email address using the code sent to the inbox.
 *
 * @param emailId           - The ID of the unverified email
 * @param verificationCode  - The verification code sent to the email address
 *
 * @returns Updated email with new verified status
 */
export const VERIFY_EMAIL_MUTATION = gql`
	mutation VerifyEmail($emailId: Int!, $verificationCode: String!) {
		verifyEmail(input: { emailId: $emailId, verificationCode: $verificationCode }) {
			email {
				address
				id
				verified
				verifiedAt
				type {
					id
					name
				}			}
		}
	}
`;

// ============================================================================
// RESEND EMAIL VERIFICATION CODE
// ============================================================================

/**
 * ADD_AND_SEND_EMAIL_VERIFICATION_CODE_MUTATION
 *
 * Resends a verification code to a previously added (but unverified) email address.
 *
 * @param emailId - The ID of the unverified email (returned from addEmails)
 *
 * @returns The email address and id for confirmation
 */
export const ADD_AND_SEND_EMAIL_VERIFICATION_CODE_MUTATION = gql`
	mutation AddAndSendEmailVerificationCode($emailId: Int!) {
		addAndSendEmailVerificationCode(input: { emailId: $emailId }) {
			email {
				address
				id
			}
		}
	}
`;
